import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../utils/jobIntake';

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) { const err = new Error((data && data.detail) || `Request failed (${res.status})`); err.status = res.status; throw err; }
  return data;
}

// HM-led job creation — what this user must act on: extra approvals (HR review / compensation) and approved
// jobs to assign to a recruiter (Receive & assign jobs). Shown at the top of the Jobs page; notifications are in
// the bell. Renders nothing when the company isn't enabled or there is nothing to act on.
function JobInbox() {
  const [inbox, setInbox] = useState(null);
  const [choice, setChoice] = useState({});         // requirement id → recruiter id
  const [comments, setComments] = useState({});     // approval id → comment
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setInbox(await api('/api/job-routing/inbox'));
    } catch (e) { setInbox(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!inbox) return null;
  const { approvals, queue, recruiters } = inbox;
  if (!approvals.length && !queue.length) return null;

  const decide = async (a, decision) => {
    setError('');
    try {
      await api(`/api/job-routing/approvals/${a.id}/decide`, { method: 'POST', body: { decision, comment: comments[a.id] || '' } });
      await load();
    } catch (e) { setError(e.message); }
  };
  const assign = async (job) => {
    setError('');
    try {
      await api(`/api/job-routing/requirements/${job.requirement_id}/assign`, { method: 'POST', body: { recruiter_id: Number(choice[job.requirement_id]) } });
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5" data-testid="job-inbox">
      <h3 className="text-lg font-black text-gray-900">Waiting for you</h3>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {approvals.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Approvals for you</p>
          {approvals.map((a) => (
            <div key={a.id} className="border border-amber-200 bg-amber-50 rounded-xl p-3 mb-2" data-testid="approval">
              <p className="text-sm font-semibold text-gray-900">{a.step_label}: {a.job_title || 'New job'}
                {a.self_approval && <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid="self-approval">Self-approval — {a.self_approval_note || 'no other approver'}</span>}</p>
              <p className="text-xs text-gray-600">Hiring manager: {a.hiring_manager_name || '—'} · version {a.version_no}</p>
              {Object.entries(a.fields || {}).map(([k, v]) => <p key={k} className="text-sm text-gray-800 mt-1">{v}</p>)}
              <input aria-label={`Comment ${a.id}`} value={comments[a.id] || ''} placeholder="Comment (required to send back)"
                onChange={(e) => setComments({ ...comments, [a.id]: e.target.value })}
                className="mt-2 w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
              <div className="mt-2 flex gap-2">
                <button onClick={() => decide(a, 'approve')} className="px-3 py-1.5 rounded-lg bg-brand-green text-white text-xs font-bold">Approve</button>
                <button onClick={() => decide(a, 'reject')} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-bold">Send back to HM</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {queue.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Approved jobs to assign</p>
          {queue.map((job) => (
            <div key={job.requirement_id} className="flex flex-wrap items-center gap-2 py-2 border-b border-gray-100" data-testid="queue-item">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{job.job_title || 'New job'}{job.routed_to_me ? ' · routed to you' : ''}</p>
                <p className="text-xs text-gray-500">Hiring manager: {job.hiring_manager_name || '—'}</p>
              </div>
              <select aria-label={`Recruiter for ${job.requirement_id}`} value={choice[job.requirement_id] || ''}
                onChange={(e) => setChoice({ ...choice, [job.requirement_id]: e.target.value })}
                className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm">
                <option value="">Choose recruiter…</option>
                {recruiters.map((r) => <option key={r.id} value={r.id}>{r.full_name} ({r.designation})</option>)}
              </select>
              <button disabled={!choice[job.requirement_id]} onClick={() => assign(job)}
                className="px-3 py-1.5 rounded-lg bg-brand-blue text-white text-xs font-bold disabled:opacity-40">Assign</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default JobInbox;
